import React, {useState} from 'react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    useDisclosure,
    Flex,
    Text,
    IconButton,
    Icon,
    Link,
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    Heading
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useGiftCardFields from '../../../components/forms/useGiftCardCheckBalanceFields'
import Field from '../../../components/field'
import {useForm} from 'react-hook-form'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3'
import GoogleRecatpchaText from '../../../components/google-recatpcha-text'
import {sendViewErrorEvent} from '../../../utils/gtm-utils'

export const GiftCardCheckBalance = ({isOpen, onClose}) => {
    const {formatMessage} = useIntl()
    const {app: appConfig} = getConfig()
    const form = useForm()
    const fields = useGiftCardFields({form})
    const {currency} = useCurrency()
    const [balance, setBalance] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const {getTokenWhenReady} = useAccessToken()
    const {executeRecaptcha} = useGoogleReCaptcha()
    const proxyPath = appConfig.commerceAPI.proxyPath
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId

    const onSubmit = async (data) => {
        setIsLoading(true)
        const {cardNumber, pin} = data

        try {
            // Get reCAPTCHA token
            let recaptchaToken = null
            if (executeRecaptcha) {
                try {
                    recaptchaToken = await executeRecaptcha('gc_balance')
                } catch (error) {
                    console.error('reCAPTCHA error:', error)
                }
            } else {
                console.warn('reCAPTCHA not available - proceeding without verification')
            }

            const token = await getTokenWhenReady()
            const response = await fetch(
                `${proxyPath}/custom/smartclixx/v1/organizations/${organizationId}/check-balance?siteId=${siteId}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        cardNumber,
                        pin,
                        ...(recaptchaToken && {recaptchaToken})
                    })
                }
            )

            const res = await response.json()

            if (response.ok && res.success) {
                setBalance(res.amount)
            } else {
                const errorMessage = res.message || 'Failed to check balance.'
                let gtmMsg = errorMessage

                if (errorMessage.startsWith('DECLINE Inv Card')) {
                    gtmMsg = 'Please enter a valid gift card number'
                    form.setError('cardNumber', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'checkout_gc.invalid',
                            defaultMessage: 'Please enter a valid gift card number'
                        })
                    })
                } else if (errorMessage.startsWith('DECLINE Inv CVV2')) {
                    gtmMsg = 'Enter a valid gift card pin'
                    form.setError('pin', {
                        type: 'manual',
                        message: formatMessage({
                            id: 'checkout_gc.invalid_card_pin',
                            defaultMessage: 'Enter a valid gift card pin'
                        })
                    })
                } else {
                    form.setError('cardNumber', {
                        type: 'manual',
                        message: errorMessage
                    })
                }
                // GTM Modification
                sendViewErrorEvent(gtmMsg)

                setBalance(0)
            }

            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
            form.setError('cardNumber', {
                type: 'manual',
                message: 'An unexpected error occurred. Please try again.'
            })
        }
    }

    return (
        <>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
                <DrawerOverlay />
                <DrawerContent maxW="490px">
                    <DrawerCloseButton />

                    <DrawerBody>
                        <form id="gift-card-check-balance" onSubmit={form.handleSubmit(onSubmit)}>
                            <Stack spacing={8}>
                                <Heading
                                    as="h3"
                                    fontWeight="bold"
                                    textTransform="uppercase"
                                    fontSize="xl"
                                    mt="56px"
                                >
                                    <FormattedMessage
                                        id="gift_card.check_balance.title"
                                        defaultMessage="Check Balance"
                                    />
                                </Heading>
                                <Text>
                                    <FormattedMessage
                                        id="gift_card.check_balance.description"
                                        defaultMessage="Checking your Gift Card balance is easy. Simply enter your number and PIN."
                                    />
                                </Text>
                                <Box>
                                    <Field {...fields.cardNumber} />
                                    <Field {...fields.pin} />
                                </Box>
                                <Flex
                                    alignItems="center"
                                    justifyContent="space-between"
                                    bg="gray.200"
                                    p="23px 16px"
                                    flexDirection={{base: 'column', lg: 'row'}}
                                >
                                    <Text fontSize="xs">
                                        <FormattedMessage
                                            id="gift_card.check_balance.your_balance"
                                            defaultMessage="Your Balance"
                                        />
                                    </Text>
                                    <Text fontSize="3xl" fontWeight="bold">
                                        <FormattedNumber
                                            value={balance}
                                            style="currency"
                                            currency={currency}
                                        />
                                    </Text>
                                </Flex>
                                <Stack spacing={2}>
                                    <Button
                                        type="submit"
                                        form="gift-card-check-balance"
                                        width="full"
                                        isLoading={isLoading}
                                        loadingText={formatMessage({
                                            id: 'gift_card.check_balance.loading',
                                            defaultMessage: 'Checking Balance...'
                                        })}
                                        variant="solid"
                                        mt={4}
                                    >
                                        <FormattedMessage
                                            id="gift_card.check_balance.button"
                                            defaultMessage="Check Balance"
                                        />
                                    </Button>
                                    <GoogleRecatpchaText />
                                </Stack>
                            </Stack>
                        </form>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}
